import { Injectable } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

/** Lazy route segments for sidebar destinations — preload as soon as the shell is ready. */
const PRELOAD_PATHS = new Set([
  'dashboard',
  'directives',
  'revocable-trusts',
  'your-plan',
  'emergency-binder',
  'contacts',
  'files',
  'shared-with-me',
  'secrets-vault',
  'recovery-agents',
  'inheritance-plan',
  'disbursement',
  'online-accounts',
  'time-capsule',
  'profile',
  'permissions',
  'asset-portfolio',
  'support',
  'business-dashboard',
  'legacy-score',
  'clients',
  'advisors',
  'offices',
  'services',
  'client-checklist',
  'referrals',
  'help',
  'wills',
  'guardians',
  'cl-dashboard',
]);

@Injectable({ providedIn: 'root' })
export class SelectivePreloadingStrategy implements PreloadingStrategy {
  private readonly loadFns = new Map<string, () => Observable<unknown>>();
  private readonly loaded = new Set<string>();

  preload(route: Route, load: () => Observable<unknown>): Observable<unknown> {
    const path = route.path || '';
    if (route.loadChildren) {
      this.loadFns.set(path, load);
    }

    const shouldPreload =
      route.data?.['preload'] === true || PRELOAD_PATHS.has(path);

    if (!shouldPreload || this.loaded.has(path)) {
      return of(null);
    }

    return load().pipe(
      tap(() => this.loaded.add(path)),
      catchError(() => of(null))
    );
  }

  /** Warm lazy chunk when the user hovers a sidebar row (before click). */
  preloadPathForMenu(menuUrl: string): void {
    const lazyKey = SelectivePreloadingStrategy.lazyKeyFromMenuUrl(menuUrl);
    if (!lazyKey || this.loaded.has(lazyKey)) {
      return;
    }
    const load = this.loadFns.get(lazyKey);
    if (load) {
      load()
        .pipe(tap(() => this.loaded.add(lazyKey)))
        .subscribe({ error: () => {} });
    }
  }

  private static lazyKeyFromMenuUrl(menuUrl: string): string | null {
    const segments = (menuUrl || '')
      .split('?')[0]
      .replace(/\/$/, '')
      .replace(/^\//, '')
      .split('/')
      .filter(Boolean);

    if (!segments.length) {
      return null;
    }

    if (segments[0] === 'business' && segments[1]) {
      return segments[1];
    }

    return segments[0];
  }
}
